import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Spinner, WarningCircle, LinkSimple } from "@phosphor-icons/react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Public read-only audit report page. Accessed via a share token embedded
 * in outreach emails: `${APP_URL}/audit/<token>`. No auth required — access
 * control is via the unguessable 128-bit token.
 */
export default function PublicAudit() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_BASE}/api/public/audit/${token}`)
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => !cancelled && setErr(e?.response?.data?.detail || "This audit link is invalid or has expired."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center">
        <Spinner size={22} className="animate-spin text-orange-500" />
      </div>
    );
  }

  if (err || !data) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center px-6">
        <div className="max-w-md text-center" data-testid="public-audit-error">
          <WarningCircle size={40} className="text-red-400 mx-auto mb-4" weight="duotone" />
          <div className="font-display text-xl mb-2">Audit not available</div>
          <div className="text-sm text-zinc-400">{err || "This audit link is invalid or has expired."}</div>
        </div>
      </div>
    );
  }

  const generatedDate = data.generated_at
    ? new Date(data.generated_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
    : null;
  const preparedBy = data.prepared_by?.name;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="public-audit-page">
      {/* Header */}
      <div className="border-b border-zinc-800 bg-zinc-900/40">
        <div className="max-w-3xl mx-auto px-6 py-8">
          <div className="text-[11px] font-mono uppercase tracking-widest text-orange-500 mb-2">
            Website & Presence Audit
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-zinc-50" data-testid="audit-business-name">
            {data.business_name}
          </h1>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-sm text-zinc-400">
            {data.industry && <span>{data.industry}</span>}
            {data.city && (
              <span className="text-zinc-600">
                · {data.city}
                {data.state ? `, ${data.state}` : ""}
              </span>
            )}
            {data.website && (
              <a
                href={data.website.startsWith("http") ? data.website : `https://${data.website}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="audit-website-link"
                className="text-orange-400 hover:text-orange-300 inline-flex items-center gap-1"
              >
                <LinkSimple size={12} /> {data.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
              </a>
            )}
          </div>
          {(preparedBy || generatedDate) && (
            <div className="text-[11px] font-mono text-zinc-500 mt-4">
              {preparedBy && <>Prepared by <span className="text-zinc-300">{preparedBy}</span></>}
              {preparedBy && generatedDate && " · "}
              {generatedDate}
            </div>
          )}
        </div>
      </div>

      {/* Report body */}
      <div className="max-w-3xl mx-auto px-6 py-10">
        <pre
          className="whitespace-pre-wrap font-mono text-sm text-zinc-200 leading-relaxed bg-zinc-900/40 border border-zinc-800 rounded-sm p-6"
          data-testid="audit-body"
        >
          {data.audit_report}
        </pre>

        {/* Footer CTA */}
        {preparedBy && (
          <div className="mt-12 pt-8 border-t border-zinc-800 text-center" data-testid="audit-footer">
            <div className="text-sm text-zinc-400 mb-2">
              This audit was prepared by <span className="text-zinc-100 font-medium">{preparedBy}</span>.
            </div>
            {data.prepared_by?.website && (
              <a
                href={data.prepared_by.website.startsWith("http") ? data.prepared_by.website : `https://${data.prepared_by.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-400 hover:text-orange-300 text-sm inline-flex items-center gap-1"
              >
                <LinkSimple size={12} /> Learn more
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
